import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ZoomIn, ZoomOut } from 'lucide-react'
import type { DayData, Segment } from './types'
import { sampleAt, timeLabel, wallToMedia } from './time'
import { fetchPreview } from './api'
import type { TimelineZoom } from './VodPlayer'
import { usePosterPreload } from './usePosterPreload'
import type { ArchiveEvent } from './events'
import { speechStatus } from './voice'
import { analysisTimeline } from '@vod/core/playback'
import { rangeFirst } from './timelineExperiment'
import { DayOverview } from './DayOverview'
import { timelineSegments } from './timelineSegments'

function rulerLabel(utc: number, timezone: string, seconds = false, zone = false) {
  const label = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(seconds ? { second: '2-digit' } : {}),
    ...(zone ? { timeZoneName: 'short' } : {}),
  }).format(utc)
  return (seconds ? label : label.replace(/:00\b/, '')).replace(/AM|PM/g, (value) =>
    value.toLowerCase(),
  )
}

// Retain a bounded set of decoded images so revisiting a sheet can render immediately.
const decodedSheets = new Map<string, HTMLImageElement>()

function cacheSheet(url: string, image: HTMLImageElement) {
  decodedSheets.delete(url)
  decodedSheets.set(url, image)
  while (decodedSheets.size > 36) {
    decodedSheets.delete(decodedSheets.keys().next().value!)
  }
}

function PreviewImage({
  url,
  poster,
  fallback,
  onDecoded,
  width,
  height,
  columns,
  rows,
  tile,
}: {
  url?: string
  poster?: string | null
  fallback?: { url: string; tile: number }
  onDecoded: () => void
  width: number
  height: number
  columns: number
  rows: number
  tile: number
}) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(() => {
    const cached = url ? decodedSheets.get(url) : undefined
    return cached?.complete && cached.naturalWidth > 0 ? 'ready' : 'loading'
  })
  useEffect(() => {
    const cached = url ? decodedSheets.get(url) : undefined
    if (url && cached) {
      decodedSheets.delete(url)
      decodedSheets.set(url, cached)
    }
  }, [url])
  return (
    <div className="preview-image" style={{ width, height }}>
      {url && (
        <img
          key="sprite"
          alt="Recording preview"
          src={url}
          onLoad={async (event) => {
            const image = event.currentTarget
            try {
              await image.decode()
              cacheSheet(url, image)
              onDecoded()
              if (image.isConnected) setStatus('ready')
            } catch {
              decodedSheets.delete(url)
              if (image.isConnected) setStatus('error')
            }
          }}
          onError={() => {
            decodedSheets.delete(url)
            setStatus('error')
          }}
          draggable={false}
          style={{
            visibility: status === 'ready' ? 'visible' : 'hidden',
            width: width * columns,
            height: height * rows,
            maxWidth: 'none',
            transform: `translate(${-width * (tile % columns)}px, ${-height * Math.floor(tile / columns)}px)`,
          }}
        />
      )}
      {(!url || status !== 'ready') &&
        (fallback ? (
          <img
            key="fallback"
            src={fallback.url}
            alt="Nearest recording preview"
            draggable={false}
            style={{
              width: width * columns,
              height: height * rows,
              maxWidth: 'none',
              transform: `translate(${-width * (fallback.tile % columns)}px, ${-height * Math.floor(fallback.tile / columns)}px)`,
            }}
          />
        ) : poster ? (
          <img
            key="poster"
            className="poster-preview"
            src={poster}
            alt="Recording poster"
            draggable={false}
          />
        ) : (
          <span>
            {url && status === 'loading' ? 'Loading preview…' : 'Preview unavailable'}
          </span>
        ))}
    </div>
  )
}

function Preview({
  segment,
  utc,
  sampleUtc = utc,
  timezone,
  width,
}: {
  segment?: Segment
  utc: number
  sampleUtc?: number
  timezone: string
  width: number
}) {
  const recording = segment?.recording
  const [, setDecodedVersion] = useState(0)
  const onDecoded = useCallback(() => setDecodedVersion((version) => version + 1), [])
  const query = useQuery({
    queryKey: ['preview', recording?.previewId, recording?.previewUrl],
    enabled: Boolean(recording?.previewUrl),
    staleTime: Infinity,
    queryFn: ({ signal }) => fetchPreview(recording!.previewUrl!, recording!.id, signal),
  })
  const manifest = query.data
  const sample =
    manifest && recording
      ? sampleAt(
          manifest.samples,
          wallToMedia(analysisTimeline(recording), sampleUtc) * 1000,
        )
      : null
  const sheet = sample && manifest ? manifest.sheets[sample.sheet] : null
  const mediaMs = recording
    ? wallToMedia(analysisTimeline(recording), sampleUtc) * 1000
    : 0
  let fallback: { url: string; tile: number } | undefined
  let nearestDistance = Infinity
  for (const candidate of manifest?.samples ?? []) {
    const url = manifest!.sheets[candidate.sheet]!.url
    const cached = decodedSheets.get(url)
    const distance = Math.abs(candidate.media_ms - mediaMs)
    if (cached?.complete && cached.naturalWidth > 0 && distance < nearestDistance) {
      fallback = { url, tile: candidate.tile }
      nearestDistance = distance
    }
  }
  useEffect(() => {
    if (!manifest || !sample) return
    let active = true
    const images = [sample.sheet - 1, sample.sheet + 1].flatMap((index) => {
      const adjacent = manifest.sheets[index]
      if (!adjacent || decodedSheets.has(adjacent.url)) return []
      const image = new Image()
      image.src = adjacent.url
      void image
        .decode()
        .then(() => {
          if (!active) return
          cacheSheet(adjacent.url, image)
          onDecoded()
        })
        .catch(() => {})
      return [image]
    })
    return () => {
      active = false
      for (const image of images) image.src = ''
    }
  }, [manifest, sample?.sheet, onDecoded])
  const maxHeight = (width * 9) / 16
  const aspect = manifest ? manifest.tile_width / manifest.tile_height : 16 / 9
  const imageWidth = Math.min(width, maxHeight * aspect)
  const height = imageWidth / aspect
  return (
    <div className="preview-card" data-testid="scrub-preview">
      <div style={{ width, height: maxHeight, display: 'grid', placeItems: 'center' }}>
        <PreviewImage
          key={`${recording?.id}:${sheet?.url}`}
          url={sheet?.url}
          poster={recording?.posterUrl}
          fallback={fallback}
          onDecoded={onDecoded}
          width={imageWidth}
          height={height}
          columns={manifest?.columns ?? 1}
          rows={manifest?.rows ?? 1}
          tile={sample?.tile ?? 0}
        />
      </div>
      <div className="preview-caption">
        <strong>{timeLabel(utc, timezone, true)}</strong>
        {recording && speechStatus(recording, sampleUtc) === 'Speech region' && (
          <div className="speech-caption">Speech</div>
        )}
        <span>{!recording && 'NO FOOTAGE'}</span>
      </div>
    </div>
  )
}

export function Timeline({
  data,
  segments,
  clock,
  buffered,
  onSeek: seek,
  defaultZoom = 'day',
  timelapseSpeed,
  events = [],
  speech = [],
  compact = false,
  initialRange,
  requestedView,
  timestamp,
}: {
  data: DayData
  segments: Segment[]
  clock: number
  buffered: [number, number][]
  onSeek: (utc: number) => void
  defaultZoom?: TimelineZoom
  timelapseSpeed?: number
  events?: ArchiveEvent[]
  speech?: Segment[]
  compact?: boolean
  initialRange?: { start: number; end: number }
  requestedView?: { start: number; end: number }
  timestamp?: ReactNode
}) {
  const displaySegments = useMemo(() => timelineSegments(segments), [segments])
  const following = useRef(true)
  function onSeek(utc: number) {
    following.current = true
    seek(utc)
  }
  const [initialZoom] = useState<number | 'day'>(() => {
    try {
      const stored = localStorage.getItem('vod:timeline-zoom')
      if (stored === 'day') return 'day'
      const length = Number(stored)
      if (Number.isFinite(length) && length >= 60000 && length <= 25 * 3600000)
        return length
    } catch {
      // Storage may be unavailable in embedded players.
    }
    return defaultZoom === 'day'
      ? 'day'
      : { '6h': 21600000, '1h': 3600000, '10m': 600000 }[defaultZoom]
  })
  const preferredZoom = useRef(initialZoom)
  const [initialOffset] = useState<number | null>(() => {
    try {
      const stored = localStorage.getItem('vod:timeline-offset')
      const offset = Number(stored)
      if (
        stored !== null &&
        Number.isFinite(offset) &&
        offset >= 0 &&
        offset < 25 * 3600000
      )
        return offset
    } catch {
      // Storage may be unavailable in embedded players.
    }
    return null
  })
  const preferredOffset = useRef(initialOffset)
  const initialized = useRef(false)
  const [detailView, setDetailView] = useState(
    initialRange ?? { start: data.start, end: data.end },
  )
  function setView(next: { start: number; end: number }) {
    const offset = next.start - data.start
    if (preferredOffset.current !== offset) {
      preferredOffset.current = offset
      try {
        localStorage.setItem('vod:timeline-offset', String(offset))
      } catch {
        // Keep the in-memory preference when storage is unavailable.
      }
    }
    const length = next.end - next.start
    const zoom = length === data.end - data.start ? 'day' : length
    if (preferredZoom.current !== zoom) {
      preferredZoom.current = zoom
      try {
        localStorage.setItem('vod:timeline-zoom', String(zoom))
      } catch {
        // Keep the in-memory preference when storage is unavailable.
      }
    }
    setDetailView(next)
  }
  const view = compact && !rangeFirst ? { start: data.start, end: data.end } : detailView
  useEffect(() => {
    if (requestedView) {
      following.current = false
      setView(requestedView)
    }
  }, [requestedView])
  const [hover, setHover] = useState<number | null>(null)
  usePosterPreload(segments, hover ?? clock)
  const [dragging, setDragging] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const seekTarget = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLElement>(null)
  const [width, setWidth] = useState(1000)
  const previewWidth = Math.max(1, Math.min(512, width - 10))
  const previewInset = (previewWidth + 10) / 2
  const viewRef = useRef(view)
  viewRef.current = view
  const pointers = useRef(new Map<number, number>())
  const pinch = useRef<{
    distance: number
    view: typeof view
    anchor: number
    fraction: number
  } | null>(null)
  const panning = useRef<{ x: number; view: typeof view; overview: boolean } | null>(null)
  const didPinch = useRef(false)
  const span = view.end - view.start
  const clamp = (start: number, length: number) => {
    length = Math.max(60000, Math.min(data.end - data.start, length))
    start = Math.max(data.start, Math.min(data.end - length, start))
    return { start, end: start + length }
  }
  function zoom(length: number, anchor = clock, fraction = 0.5) {
    following.current = true
    setView(clamp(anchor - length * fraction, length))
  }
  function utcAt(x: number) {
    const rect = root.current!.getBoundingClientRect(),
      v = viewRef.current
    return Math.max(
      v.start,
      Math.min(v.end - 1, v.start + ((x - rect.left) / rect.width) * (v.end - v.start)),
    )
  }
  function isSeekPoint(x: number, y: number) {
    const rect = seekTarget.current!.getBoundingClientRect()
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  }
  useEffect(() => {
    const range = !initialized.current ? initialRange : undefined
    initialized.current = true
    following.current = !range && preferredOffset.current === null
    const length =
      preferredZoom.current === 'day' ? data.end - data.start : preferredZoom.current
    if (range && range.end > data.start && range.start < data.end) setView(range)
    else {
      const next = clamp(
        preferredOffset.current === null
          ? clock - length / 2
          : data.start + preferredOffset.current,
        length,
      )
      setDetailView(next)
    }
    setHover(null)
  }, [data.day, data.start, data.end])
  useEffect(() => {
    if (!root.current) return
    const observer = new ResizeObserver((entries) =>
      setWidth(entries[0].contentRect.width),
    )
    observer.observe(root.current)
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    if (dragging || hover !== null || clock < data.start || clock >= data.end) return
    if (view.end <= data.start || view.start >= data.end) return
    if (clock >= view.start && clock < view.end) following.current = true
    if (!following.current) return
    if (clock < view.start || clock >= view.end) setView(clamp(clock - span / 2, span))
  }, [clock])
  useEffect(() => {
    const element = panel.current
    if (!element) return
    const wheel = (event: WheelEvent) => {
      if (compact) return
      event.preventDefault()
      following.current = false
      const v = viewRef.current,
        length = v.end - v.start
      if (event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY))
        setView(
          clamp(
            v.start + ((event.deltaX || event.deltaY) / element.clientWidth) * length,
            length,
          ),
        )
      else {
        const rect = root.current!.getBoundingClientRect(),
          fraction = (event.clientX - rect.left) / rect.width
        const nextLength = Math.max(
          60000,
          Math.min(data.end - data.start, length * Math.exp(event.deltaY * 0.004)),
        )
        setView(clamp(v.start + length * fraction - nextLength * fraction, nextLength))
      }
    }
    element.addEventListener('wheel', wheel, { passive: false })
    return () => element.removeEventListener('wheel', wheel)
  }, [data.start, data.end, compact])
  const desiredStep = span / Math.max(1, width / 80)
  const step =
    [
      10000, 30000, 60000, 300000, 900000, 1800000, 3600000, 7200000, 10800000, 21600000,
    ].find((step) => step >= desiredStep) ?? 21600000
  const ticks: number[] = []
  for (
    let t = data.start + Math.ceil((view.start - data.start) / step) * step;
    t <= view.end;
    t += step
  )
    ticks.push(t)
  const position = (utc: number) => ((utc - view.start) / span) * 100
  const previewSegment =
    hover === null
      ? undefined
      : displaySegments.find((s) => hover >= s.start && hover < s.end)
  const showZone = data.end - data.start !== 24 * 3600000
  const rangeTime = new Intl.DateTimeFormat('en-US', {
    timeZone: data.timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return (
    <section
      ref={panel}
      className={`timeline-panel ${compact ? 'timeline-compact' : ''}`}
      aria-label="Recording timeline"
      onPointerDown={(e) => {
        const target = e.target as HTMLElement
        if (target.closest('button, select, .calendar-popover, .picker-dialog')) return
        if (e.button > 1) return
        if (compact && !isSeekPoint(e.clientX, e.clientY)) return
        e.preventDefault()
        target.closest<HTMLElement>('[role="slider"]')?.focus({ preventScroll: true })
        e.currentTarget.setPointerCapture(e.pointerId)
        pointers.current.set(e.pointerId, e.clientX)
        const rect = root.current!.getBoundingClientRect()
        if (pointers.current.size === 2 && !compact) {
          const [a, b] = [...pointers.current.values()]
          pinch.current = {
            distance: Math.abs(a - b),
            view: viewRef.current,
            anchor: utcAt((a + b) / 2),
            fraction: ((a + b) / 2 - rect.left) / rect.width,
          }
          following.current = false
          didPinch.current = true
          setHover(null)
        } else if (
          !compact &&
          (e.shiftKey || e.button === 1 || !isSeekPoint(e.clientX, e.clientY))
        ) {
          following.current = false
          const overview = !!(e.target as HTMLElement).closest('.overview-track')
          let next = viewRef.current
          if (overview) {
            const utc =
              data.start +
              ((e.clientX - rect.left) / rect.width) * (data.end - data.start)
            if (utc < next.start || utc > next.end) {
              next = clamp(utc - span / 2, span)
              setView(next)
            }
          }
          panning.current = { x: e.clientX, view: next, overview }
          setHover(null)
        } else {
          didPinch.current = false
          setDragging(true)
          setHover(utcAt(e.clientX))
        }
      }}
      onPointerMove={(e) => {
        if (pointers.current.has(e.pointerId))
          pointers.current.set(e.pointerId, e.clientX)
        const rect = root.current!.getBoundingClientRect()
        if (pinch.current && pointers.current.size === 2) {
          const [a, b] = [...pointers.current.values()],
            p = pinch.current
          const length =
            ((p.view.end - p.view.start) * p.distance) / Math.max(20, Math.abs(a - b))
          const fraction = ((a + b) / 2 - rect.left) / rect.width
          setView(clamp(p.anchor - length * fraction, length))
        } else if (panning.current) {
          const p = panning.current
          const delta = (e.clientX - p.x) / rect.width
          setView(
            clamp(
              p.view.start +
                delta *
                  (p.overview ? data.end - data.start : -(p.view.end - p.view.start)),
              p.view.end - p.view.start,
            ),
          )
        } else if (!didPinch.current) {
          setHover(
            dragging || isSeekPoint(e.clientX, e.clientY) ? utcAt(e.clientX) : null,
          )
        }
      }}
      onPointerUp={(e) => {
        if (!pointers.current.has(e.pointerId)) return
        if (dragging && !didPinch.current && !panning.current) onSeek(utcAt(e.clientX))
        pointers.current.delete(e.pointerId)
        if (!pointers.current.size) {
          pinch.current = null
          panning.current = null
          didPinch.current = false
          setDragging(false)
          setHover(null)
        }
      }}
      onLostPointerCapture={(event) => {
        if (!pointers.current.has(event.pointerId)) return
        pointers.current.clear()
        pinch.current = null
        panning.current = null
        didPinch.current = false
        setDragging(false)
        setHover(null)
      }}
      onPointerLeave={() => {
        if (!dragging) setHover(null)
      }}
    >
      <div className="timeline-heading">
        <div className="stamp-time-row">
          {timestamp ?? (
            <span className="timeline-range-label">
              {rangeTime.format(view.start).toLowerCase()} -{' '}
              {rangeTime.format(view.end).toLowerCase()}
            </span>
          )}
          {timelapseSpeed !== undefined && (
            <>
              <span className="stamp-speed" aria-hidden="true">
                ·
              </span>
              <span className="stamp-speed">
                {timelapseSpeed.toFixed(timelapseSpeed % 1 ? 1 : 0)}x
              </span>
            </>
          )}
        </div>
        <div className="zoom-controls">
          {[
            ['Day', data.end - data.start],
            ['6h', 6 * 3600000],
            ['1h', 3600000],
            ['10m', 600000],
          ].map(([name, ms]) => (
            <button
              key={name}
              className={Math.abs(span - Number(ms)) < 2 ? 'selected' : ''}
              onClick={() => zoom(Number(ms))}
              aria-label={`Show ${name} timeline`}
            >
              {name}
            </button>
          ))}
          <span className="divider" />
          <button aria-label="Zoom out" onClick={() => zoom(span * 2)}>
            <ZoomOut size={16} />
          </button>
          <button aria-label="Zoom in" onClick={() => zoom(span / 2)}>
            <ZoomIn size={16} />
          </button>
        </div>
      </div>
      {rangeFirst && !compact && (
        <DayOverview
          data={data}
          segments={displaySegments}
          view={view}
          onChange={(next) => {
            following.current = false
            setView(next)
          }}
        />
      )}
      <div
        ref={root}
        className={`time-ruler ${dragging ? 'is-dragging' : ''}`}
        role="slider"
        tabIndex={0}
        aria-label="Recording time"
        aria-valuemin={data.start}
        aria-valuemax={data.end - 1}
        aria-valuenow={Math.round(Math.max(data.start, Math.min(data.end - 1, clock)))}
        aria-valuetext={timeLabel(clock, data.timezone, true, true)}
        onKeyDown={(e) => {
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
            e.preventDefault()
            onSeek(
              e.key === 'Home'
                ? data.start
                : e.key === 'End'
                  ? data.end - 1
                  : clock +
                    (e.key === 'ArrowRight' ? 1 : -1) * (e.shiftKey ? 600000 : 60000),
            )
          }
        }}
      >
        <div ref={seekTarget} className="timeline-seek-target" aria-hidden="true" />
        <div className="ruler-ticks">
          {ticks.map((t) => (
            <div key={t} className="tick" style={{ left: `${position(t)}%` }}>
              <span>{rulerLabel(t, data.timezone, step < 60000, showZone)}</span>
            </div>
          ))}
        </div>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 5,
            overflow: 'hidden',
          }}
        >
          {events
            .filter((e) => e.end > view.start && e.start < view.end)
            .map((e) => (
              <div
                key={e.id}
                title={e.title}
                style={{
                  position: 'absolute',
                  top: 0,
                  height: 5,
                  background: 'var(--accent)',
                  left: `${position(Math.max(view.start, e.start))}%`,
                  width: `${((Math.min(view.end, e.end) - Math.max(view.start, e.start)) / span) * 100}%`,
                  minWidth: 2,
                }}
              />
            ))}
        </div>
        <div className="coverage-track">
          {displaySegments
            .filter((s) => s.end > view.start && s.start < view.end)
            .map((s) => (
              <div
                key={`${s.recording.id}-${s.start}`}
                className={`coverage ${s.recording.playbackKind === 'archive' ? 'is-archive' : s.recording.previewId ? 'has-preview' : ''}`}
                style={{
                  left: `${position(Math.max(view.start, s.start))}%`,
                  width: `${((Math.min(view.end, s.end) - Math.max(view.start, s.start)) / span) * 100}%`,
                }}
              />
            ))}
          {buffered.map(([start, end], i) =>
            end > view.start && start < view.end ? (
              <div
                key={i}
                className="buffered"
                style={{
                  left: `${position(Math.max(view.start, start))}%`,
                  width: `${((Math.min(view.end, end) - Math.max(view.start, start)) / span) * 100}%`,
                }}
              />
            ) : null,
          )}
          <div className="speech-track" aria-hidden="true">
            {speech
              .filter((s) => s.end > view.start && s.start < view.end)
              .map((s) => (
                <div
                  key={`${s.recording.id}-${s.start}-${s.end}`}
                  className="speech-region"
                  style={{
                    left: `${position(Math.max(view.start, s.start))}%`,
                    width: `${((Math.min(view.end, s.end) - Math.max(view.start, s.start)) / span) * 100}%`,
                  }}
                />
              ))}
          </div>
        </div>
        {clock >= view.start && clock < view.end && (
          <div className="playhead" style={{ left: `${position(clock)}%` }}>
            <i />
          </div>
        )}
        {hover !== null && (
          <div className="hover-cursor" style={{ left: `${position(hover)}%` }} />
        )}
        {hover !== null && (
          <div
            className="preview-position"
            style={{
              left: `clamp(${previewInset}px, ${position(hover)}%, calc(100% - ${previewInset}px))`,
            }}
          >
            <Preview
              segment={previewSegment}
              utc={hover}
              sampleUtc={
                previewSegment ? Math.max(hover, previewSegment.previewStart) : hover
              }
              timezone={data.timezone}
              width={previewWidth}
            />
          </div>
        )}
      </div>
    </section>
  )
}
